/**
 * Central definition of all roles.
 * Each role defines its name, scopes, hierarchy level, and whether it can update roles.
 *
 * @typedef {Object} RoleDef
 * @property {string} name
 * @property {string[] | null} scopes
 * @property {number} level
 * @property {boolean} canUpdateRoles
 */

/** @type {Record<string, RoleDef>} */
const ROLES = {
	admin: {
		name: 'Administrator',
		scopes: ['*'],
		level: 6,
		canUpdateRoles: true,
	},
	departmentAdmin: {
		name: 'Avdelingsleder',
		scopes: [
			'tenants:*',
			'documentNodes:*',
			'signatures:*',
			'companyContracts:*',
			'companyRoutines:*',
			'companyControls:*',
			'companyAssignmentTasks:*',
			'companyTodos:*',
			'companyLogs:*',
			'files:*',
			'comments:*',
			'users:*',
			'departments:read',
			'roles:read',
			'assignmentRisks:*',
			'statistics:read',
			'tenantCategories:read',
			'companyClassificationCodes:read',
			'todos:*',
			'helpPages:read',
			'governmentDeadlines:read',
			'auditingcompanies:*',
			'companies:*',
			'companyForms:*',
			'publicCompanies:*',
			'publicDeviations:*',
		],
		level: 5,
		canUpdateRoles: true,
	},
	accountant: {
		name: 'Regnskapsfører',
		scopes: [
			'assignmentRisks:*',
			'statistics:read',
			'documentNodes:*',
			'signatures:*',
			'tenants:*',
			'tenantCategories:read',
			'companyClassificationCodes:read',
			'companyContracts:*',
			'companyRoutines:*',
			'companyControls:*',
			'companyAssignmentTasks:*',
			'companyTodos:*',
			'companyLogs:*',
			'todos:*',
			'roles:read',
			'files:*',
			'comments:*',
			'users:*',
			'departments:read',
			'auditingcompanies:*',
			'helpPages:read',
			'governmentDeadlines:read',
			'companies:*',
			'companyForms:*',
			'publicCompanies:*',
			'publicDeviations:*',
		],
		level: 4,
		canUpdateRoles: true,
	},
	auditor: {
		name: 'Revisor',
		scopes: [
			'assignmentRisks:*',
			'documentNodes:*',
			'signatures:*',
			'tenants:read',
			'tenantCategories:read',
			'companyContracts:read',
			'roles:read',
			'files:*',
			'comments:*',
			'users:readBasicInfo',
			'users:readOwnUser',
			'users:updateOwnUser',
			'auditingcompanies:read',
		],
		level: 3,
		canUpdateRoles: false,
	},
	user: {
		name: 'Bruker',
		scopes: [
			'documentNodes:*',
			'signatures:*',
			'tenants:read',
			'companyContracts:read',
			'roles:read',
			'files:*',
			'users:readBasicInfo',
			'users:readOwnUser',
			'users:updateOwnUser',
			'notifications:read',
			'notifications:markAsRead',
		],
		level: 2,
		canUpdateRoles: false,
	},
	silent: {
		name: 'Inaktiv',
		scopes: null,
		level: 1,
		canUpdateRoles: false,
	},
	deleted: {
		name: 'Slettet',
		scopes: null,
		level: 0,
		canUpdateRoles: false,
	},
};

/**
 * Array of all role identifiers, in hierarchy order (lowest → highest).
 *
 * @type {string[]}
 */
export const roleKeys = Object.keys(ROLES);

/**
 * Mapping of role identifiers to their permission scopes.
 *
 * @type {Record<string, string[] | null>}
 */
export const roleScopes = Object.fromEntries(
	Object.entries(ROLES).map(([role, def]) => [role, def.scopes])
);

/**
 * Mapping of role identifiers to human-readable names.
 *
 * @type {Record<string, string>}
 */
const roleNames = Object.fromEntries(
	Object.entries(ROLES).map(([role, def]) => [role, def.name])
);

/**
 * Role hierarchy and permissions configuration.
 * Higher `level` values indicate higher privilege.
 *
 * @type {Record<string, { level: number, canUpdateRoles: boolean }>}
 */
export const roleHierarchy = Object.fromEntries(
	Object.entries(ROLES).map(([role, def]) => [
		role,
		{ level: def.level, canUpdateRoles: def.canUpdateRoles },
	])
);

/**
 * @typedef {Object} Role
 * @property {string} _id - Role identifier (same as `role`).
 * @property {string} role - Role identifier (e.g., 'admin', 'user').
 * @property {string} name - Display name of the role.
 * @property {string[] | null} scopes - Array of permission scopes, or null if none.
 * @property {number} hierarchyLevel - Hierarchy level for role comparison.
 * @property {boolean} canUpdateRoles - Whether this role can update other user roles.
 */

/**
 * All roles as an array of rich objects for easy iteration and lookup.
 *
 * @type {Role[]}
 */
export const roles = Object.entries(ROLES).map(([role, def]) => ({
	_id: role,
	role,
	name: def.name,
	scopes: def.scopes,
	hierarchyLevel: def.level,
	canUpdateRoles: def.canUpdateRoles,
}));

export default roles;

/**
 * Returns the display name for a given role identifier.
 *
 * @param {keyof typeof roleNames | string} role - The role identifier.
 * @returns {string} The display name for the role, or 'Ikke tildelt' if not assigned.
 */
export function getRoleName(role) {
	return roleNames[role] || 'Ikke tildelt';
}
